import type { Agency } from '@saaga/shared/interfaces/agencies.interface'
import type { LucideIcon } from 'lucide-react'
import { PencilIcon, Trash2Icon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { withStopPropagation } from '@/lib/event.util'

interface AgencyCardFooterProps {
  agency: Agency
  onStartChat?: (agency: Agency) => void
  onSeeDetails?: (agency: Agency) => void
  onEdit?: (agency: Agency) => void
  onDelete?: (agency: Agency) => void
}

interface IconActionProps {
  icon: LucideIcon
  tooltip: string
  className: string
  onClick: () => void
}

const IconAction = ({ icon: Icon, tooltip, className, onClick }: IconActionProps): ReactNode => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="outline"
        size="icon"
        className={className}
        onClick={withStopPropagation(onClick)}
      >
        <Icon className="size-4" />
      </Button>
    </TooltipTrigger>
    <TooltipContent>{tooltip}</TooltipContent>
  </Tooltip>
)

export const AgencyCardFooter = ({
  agency,
  onStartChat,
  onSeeDetails,
  onEdit,
  onDelete,
}: AgencyCardFooterProps): ReactNode => (
  <div className="agency-card-footer flex justify-end gap-2 flex-wrap">
    {onDelete && (
      <IconAction
        icon={Trash2Icon}
        tooltip="Delete agency"
        className="agency-card-delete text-destructive"
        onClick={() => onDelete(agency)}
      />
    )}
    {onEdit && (
      <IconAction
        icon={PencilIcon}
        tooltip="Edit agency"
        className="agency-card-edit"
        onClick={() => onEdit(agency)}
      />
    )}
    {onSeeDetails && (
      <Button
        variant="outline"
        className="agency-card-see-details flex-1 min-w-fit"
        onClick={withStopPropagation(() => onSeeDetails(agency))}
      >
        Details
      </Button>
    )}
    {onStartChat && (
      <Button
        variant="secondary"
        className="agency-card-start-chat bg-zinc-800 text-white hover:bg-zinc-700 dark:bg-zinc-200 dark:text-zinc-900 dark:hover:bg-zinc-300 flex-1 min-w-fit"
        onClick={withStopPropagation(() => onStartChat(agency))}
      >
        Start Chat
      </Button>
    )}
  </div>
)

AgencyCardFooter.displayName = 'AgencyCardFooter'
